import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, ZoomIn, ZoomOut } from "lucide-react";

export default function Timeline() {
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [zoom, setZoom] = useState(1); // 1 = decades, 2 = years

    useEffect(() => {
        api.get("/timeline", { params: { start: 1900, end: 2030 } })
            .then((r) => setData(r.data))
            .finally(() => setLoading(false));
    }, []);

    const decades = data.items || [];
    const allEncs = useMemo(() => decades.flatMap((d) => d.encounters), [decades]);

    return (
        <div className="ok-container py-10" data-testid="timeline-page">
            <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
                <div>
                    <div className="font-mono text-[10px] uppercase tracking-widest opacity-50">Chronology</div>
                    <h1 className="font-display text-3xl sm:text-4xl mt-1">Timeline of the Unknown</h1>
                    <p className="opacity-60 mt-2 max-w-xl text-sm">
                        Scroll through humanity's encounters from the modern UFO era forward. Waves emerge: 1947, 1952, 1966, 1980, 1994, 2004, 2015. Points on a much larger pattern.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => setZoom((z) => Math.max(1, z - 1))} className="ok-btn-outline" data-testid="timeline-zoom-out"><ZoomOut className="w-4 h-4" /></button>
                    <span className="font-mono text-xs opacity-60 uppercase tracking-widest">{zoom === 1 ? "Decade" : "Year"}</span>
                    <button onClick={() => setZoom((z) => Math.min(2, z + 1))} className="ok-btn-outline" data-testid="timeline-zoom-in"><ZoomIn className="w-4 h-4" /></button>
                </div>
            </div>

            {loading ? (
                <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
            ) : (
                <>
                    {/* Horizontal scroll strip */}
                    <div className="ok-card p-4 overflow-x-auto" data-testid="timeline-strip">
                        <div className="flex items-end gap-1 min-w-max">
                            {decades.map((d) => (
                                <a key={d.decade} href={`#decade-${d.decade}`} className="text-center group" data-testid={`timeline-decade-${d.decade}`}>
                                    <div className="font-mono text-[10px] opacity-60 mb-1">{d.decade}s</div>
                                    <div className="w-12 transition-all"
                                         style={{
                                             height: `${Math.min(120, 10 + d.count * 7)}px`,
                                             background: "linear-gradient(180deg, var(--ok-accent) 0%, var(--ok-surface-hover) 100%)",
                                             borderRadius: "2px",
                                         }} />
                                </a>
                            ))}
                        </div>
                    </div>

                    {/* Detail per decade */}
                    <div className="mt-10 space-y-12">
                        {decades.map((d) => (
                            <section key={d.decade} id={`decade-${d.decade}`} className="grid grid-cols-1 md:grid-cols-12 gap-6 scroll-mt-24" data-testid={`timeline-decade-section-${d.decade}`}>
                                <aside className="md:col-span-2 md:sticky md:top-20 self-start">
                                    <div className="font-display text-4xl text-[var(--ok-accent)]">{d.decade}</div>
                                </aside>
                                <div className="md:col-span-10 relative pl-6 border-l" style={{ borderColor: "var(--ok-border)" }}>
                                    {d.encounters.map((e) => (
                                        <Link key={e.id} to={`/encounters/${e.id}`} className="block relative mb-5 pb-5 last:mb-0 last:pb-0 group" data-testid={`timeline-encounter-${e.id}`}>
                                            <div className="absolute -left-[29px] top-2 w-3 h-3 rounded-full border-2 group-hover:scale-125 transition-transform"
                                                 style={{ background: "var(--ok-bg)", borderColor: e.source_type === "historical" ? "var(--ok-accent)" : "#3b82f6" }} />
                                            <div className="flex items-baseline justify-between gap-3 flex-wrap">
                                                <h3 className="font-display font-bold text-base group-hover:text-[var(--ok-accent)] transition-colors">
                                                    {e.title}
                                                </h3>
                                                <span className="font-mono text-[10px] uppercase tracking-widest opacity-50">{(e.encounter_date || "").slice(0, 10) || e.year}</span>
                                            </div>
                                            <div className="flex items-center gap-3 mt-1 text-xs opacity-70">
                                                <span>{e.country}</span>
                                                {e.entity_known_type && <span>· {e.entity_known_type}</span>}
                                                {e.encounter_type && <span>· {e.encounter_type}</span>}
                                                <span>· Evidence {e.credibility_score ?? 0}/100</span>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </section>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
}
